import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Philosophy", id: "manifesto" },
  { label: "Academy", id: "academy" },
  { label: "Stories", id: "stories" },
];

// Flip to true once Stripe account is approved (live payments)
const INTAKE_LIVE = true;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          onClick={() => go("hero")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo-button"
        >
          <span className="w-8 h-8 border border-gold/60 rotate-45 flex items-center justify-center transition-transform duration-500 group-hover:rotate-[135deg]">
            <span className="w-2 h-2 bg-gold -rotate-45 transition-transform duration-500 group-hover:-rotate-[135deg]" />
          </span>
          <span className="font-serif text-lg tracking-wide text-white">
            Elevated <span className="text-gold italic">Apex</span> Co.
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-gold transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          {INTAKE_LIVE && (
            <button
              onClick={() => navigate("/intake")}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-gold transition-colors duration-300"
              data-testid="nav-link-intake"
            >
              Intake
            </button>
          )}
          <button
            onClick={() => go("contact")}
            className="group flex items-center gap-2 bg-gold text-black font-sans text-xs font-medium uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-white transition-colors duration-300"
            data-testid="nav-consultation-cta"
          >
            Free Consultation
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-mono text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-gold transition-colors"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              {INTAKE_LIVE && (
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/intake");
                  }}
                  className="text-left font-mono text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-gold transition-colors"
                  data-testid="nav-mobile-link-intake"
                >
                  Client Intake
                </button>
              )}
              <button
                onClick={() => go("contact")}
                className="mt-2 bg-gold text-black text-xs font-medium uppercase tracking-[0.15em] px-5 py-3 text-left"
                data-testid="nav-mobile-consultation-cta"
              >
                Free Consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;